import { CITY } from '@city'
import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { AndroidInstallBanner } from './AndroidInstallBanner'
import {
  DEFAULT_LOCALE,
  isRtlLocale,
  isSupportedLocale,
  type SupportedLocale,
} from '../i18n/locales'
import { i18n } from '../i18n/config'

/**
 * Per-locale shell for the `/:locale/*` subtree.
 *
 * Guards `:locale`, syncs `<html lang>` / `<html dir>`, and changes the
 * i18next active language when the URL locale differs from the one
 * already mounted. URL is authoritative — the path-detector layer of
 * i18next reads from here, the picker / browser are weaker fallbacks.
 *
 * The prerendered HTML already emits `<title>`/`<meta>`/hreflang at
 * build time (see `scripts/prerender-locale-helper.mjs`); this layout
 * is the runtime follow-up so the hydrated SPA stays consistent with
 * the static HTML when a user navigates between locales after first
 * paint.
 */
export function LocaleLayout(): ReactElement {
  const { locale: rawLocale } = useParams<{ locale: string }>()
  const isValid = rawLocale !== undefined && isSupportedLocale(rawLocale)
  const locale: SupportedLocale = isValid ? (rawLocale as SupportedLocale) : DEFAULT_LOCALE

  // Hook order must be stable across renders — useEffect runs unconditionally,
  // but its body is a no-op while we're about to redirect away from an
  // invalid locale (StrictMode double-render safety).
  useEffect(() => {
    if (!isValid) return
    document.documentElement.lang = locale
    document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr'
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale)
    }
  }, [isValid, locale])

  if (!isValid) {
    const tail =
      typeof window !== 'undefined' && window.location.pathname.startsWith(`/${rawLocale ?? ''}`)
        ? window.location.pathname.slice(`/${rawLocale ?? ''}`.length)
        : ''
    return <Navigate to={`/${DEFAULT_LOCALE}${tail}`} replace />
  }

  // The install banner rides the locale shell so it covers every product
  // surface the way Safari's Smart App Banner does on iOS (TASK-590); it
  // renders null everywhere but Android browsers.
  return (
    <>
      {CITY.storeApps && <AndroidInstallBanner />}
      <Outlet />
    </>
  )
}
